import React, { useState } from 'react';
import AchievementCard from './AchievementCard';
import './AchievementsPanel.css';

/** Closest-to-unlocking first (ratio of progress), never-started ones last. Pure, exported for tests. */
export function nextUp(achievements, limit = 3) {
  return achievements
    .filter(a => !a.held && a.progress && a.progress.target > 0)
    .map(a => ({ a, ratio: a.progress.current / a.progress.target }))
    .sort((x, y) => y.ratio - x.ratio)
    .slice(0, limit)
    .map(x => x.a);
}

/**
 * Achievements without the clutter: a one-line summary, this week's goal, the next few to unlock, and the full
 * grouped collection behind one button. Everything shown is real progress from /portal/progression.
 */
export default function AchievementsPanel({ achievements = [], weeklyGoal, groups = [] }) {
  const [open, setOpen] = useState(false);
  const earned = achievements.filter(a => a.held);
  const upNext = nextUp(achievements);
  const pct = weeklyGoal ? Math.round((weeklyGoal.current / weeklyGoal.target) * 100) : 0;

  return (
    <div className="ap">
      <p className="ap-summary"><b>{earned.length}</b> of {achievements.length} earned</p>

      {weeklyGoal && (
        <div className={`ap-goal${weeklyGoal.done ? ' ap-goal--done' : ''}`}>
          <div className="ap-goal-top"><span className="ap-goal-title">{weeklyGoal.label}</span><b>{weeklyGoal.current}/{weeklyGoal.target}</b></div>
          <div className="ap-track" role="progressbar" aria-valuemin={0} aria-valuemax={weeklyGoal.target} aria-valuenow={weeklyGoal.current} aria-label={weeklyGoal.description}>
            <div className="ap-fill" style={{ width: `${pct}%` }} />
          </div>
          <span className="ap-goal-desc">{weeklyGoal.done ? 'Goal complete — nice work this week.' : weeklyGoal.description}</span>
        </div>
      )}

      {upNext.length > 0 && (
        <>
          <h3 className="ap-h">Next up</h3>
          <div className="progress-achievement-grid">{upNext.map(a => <AchievementCard key={a.id} achievement={a} />)}</div>
        </>
      )}

      <button type="button" className="ap-toggle" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        {open ? 'Hide all achievements' : `See all ${achievements.length} achievements`}
      </button>

      {open && groups.map(g => {
        const list = achievements.filter(a => a.category === g.key);
        if (!list.length) return null;
        return (
          <section key={g.key} className="ap-group" aria-label={g.label}>
            <h3 className="ap-h">{g.label}</h3>
            <div className="progress-achievement-grid">{list.map(a => <AchievementCard key={a.id} achievement={a} />)}</div>
          </section>
        );
      })}
    </div>
  );
}
